type FrontendDebugSinkEventKind = 'window.onerror' | 'unhandledrejection' | 'react.error_boundary';

type FrontendDebugSinkPayload = {
  event: string;
  level: 'error' | 'fatal';
  message: string;
  service: 'operator-ui';
  tenant_id: string | null;
  route_template: string;
  url: string;
  ts: string;
  build_sha: string;
  context: Record<string, unknown>;
};

const MAX_STRING_LENGTH = 700;
const DEDUPE_TTL_MS = 60_000;
const MAX_REPORTS_PER_MINUTE = 5;
const sensitiveKeyPattern = /(access[_-]?token|refresh[_-]?token|authorization|password|passwd|secret|api[_-]?key|jwt|bearer|cookie|session)/i;
const jwtPattern = /\beyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\b/g;
const botTokenPattern = /\b\d{6,}:[A-Za-z0-9_-]{20,}\b/g;
const openAiKeyPattern = /\bsk-[A-Za-z0-9_-]{16,}\b/g;

const recentReports = new Map<string, number>();
let minuteWindowStartedAt = 0;
let minuteWindowCount = 0;

function isBrowser(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

function isEnabled(): boolean {
  const envValue = String(import.meta.env.VITE_FRONTEND_DEBUGSINK_ENABLED ?? '').toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(envValue)) return true;
  if (!isBrowser()) return false;
  return window.localStorage.getItem('MG_FRONTEND_DEBUGSINK_ENABLED') === '1';
}

export function normalizeFrontendRouteTemplate(pathname: string): string {
  const raw = String(pathname || '').split('?', 1)[0].split('#', 1)[0] || '/';
  return raw
    .split('/')
    .map((part) => {
      if (/^\d+$/.test(part)) return ':id';
      if (/^[0-9a-f]{24,64}$/i.test(part)) return ':hex';
      if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(part)) return ':uuid';
      return part;
    })
    .join('/');
}

function redactString(value: string): string {
  const redacted = value
    .replace(jwtPattern, '***REDACTED_JWT***')
    .replace(botTokenPattern, '***REDACTED_BOT_TOKEN***')
    .replace(openAiKeyPattern, '***REDACTED_API_KEY***')
    .replace(/([?&])(token|sig|signature|auth|jwt|session|password)=([^&]+)/gi, '$1$2=***REDACTED***');
  if (redacted.length > MAX_STRING_LENGTH) {
    return `${redacted.slice(0, 180)}...***REDACTED_BLOB***...`;
  }
  return redacted;
}

export function sanitizeDebugSinkValue(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return redactString(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (depth >= 4) return '***REDACTED_DEPTH***';
  if (Array.isArray(value)) return value.slice(0, 30).map((item) => sanitizeDebugSinkValue(item, depth + 1));
  if (typeof value === 'object') {
    const out: Record<string, unknown> = {};
    Object.entries(value as Record<string, unknown>)
      .slice(0, 60)
      .forEach(([key, item]) => {
        out[key] = sensitiveKeyPattern.test(key) ? '***REDACTED***' : sanitizeDebugSinkValue(item, depth + 1);
      });
    return out;
  }
  return redactString(String(value));
}

function tenantFromLocation(): string | null {
  if (!isBrowser()) return null;
  const host = window.location.hostname || '';
  const first = host.split('.')[0];
  if (!first || ['moy-gorod', 'www', 'localhost', '127'].includes(first)) return null;
  return first;
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message || error.name;
  if (typeof error === 'string') return error;
  return 'frontend.error';
}

function errorContext(error: unknown): Record<string, unknown> {
  if (error instanceof Error) {
    return {
      name: error.name,
      message: error.message,
      stack: error.stack,
    };
  }
  return { reason: error };
}

export function buildFrontendDebugSinkPayload(
  kind: FrontendDebugSinkEventKind,
  error: unknown,
  extra: Record<string, unknown> = {},
): FrontendDebugSinkPayload {
  const path = isBrowser() ? `${window.location.pathname}${window.location.search || ''}` : '/';
  const routeTemplate = normalizeFrontendRouteTemplate(path);
  const message = errorMessage(error);
  return sanitizeDebugSinkValue({
    event: `frontend.${kind}`,
    level: 'error',
    message,
    service: 'operator-ui',
    tenant_id: tenantFromLocation(),
    route_template: routeTemplate,
    url: path,
    ts: new Date().toISOString(),
    build_sha: String(import.meta.env.VITE_BUILD_SHA ?? 'unknown'),
    context: {
      kind,
      route_template: routeTemplate,
      error: errorContext(error),
      ...extra,
    },
  }) as FrontendDebugSinkPayload;
}

function shouldSend(payload: FrontendDebugSinkPayload): boolean {
  const now = Date.now();
  if (now - minuteWindowStartedAt > DEDUPE_TTL_MS) {
    minuteWindowStartedAt = now;
    minuteWindowCount = 0;
  }
  if (minuteWindowCount >= MAX_REPORTS_PER_MINUTE) return false;
  const key = `${payload.event}|${payload.route_template}|${payload.message}`;
  const previous = recentReports.get(key) || 0;
  if (now - previous < DEDUPE_TTL_MS) return false;
  recentReports.set(key, now);
  minuteWindowCount += 1;
  return true;
}

export async function reportFrontendDebugSinkError(
  kind: FrontendDebugSinkEventKind,
  error: unknown,
  extra: Record<string, unknown> = {},
): Promise<boolean> {
  if (!isEnabled()) return false;
  const payload = buildFrontendDebugSinkPayload(kind, error, extra);
  if (!shouldSend(payload)) return false;
  try {
    const { default: api } = await import('@/services/api');
    await api.post('/api/ui/frontend-debug-sink', { payload });
    return true;
  } catch {
    return false;
  }
}

export function installFrontendDebugSinkReporter(): void {
  if (!isBrowser()) return;
  window.addEventListener('error', (event) => {
    void reportFrontendDebugSinkError('window.onerror', event.error || event.message, {
      source: event.filename,
      line: event.lineno,
      column: event.colno,
    });
  });
  window.addEventListener('unhandledrejection', (event) => {
    void reportFrontendDebugSinkError('unhandledrejection', event.reason);
  });
}
